// Тапы по датам после выбора заезда (T37): справа от заезда — выезды (и смена уже выбранного),
// слева — перенос заезда; недоступная дата объясняет причину и выбор не трогает. Чистая логика
// без DOM — index.js применяет результат к store.
import { addDays, humanDay, nightsWord, adultsWord, plural } from "./dates.js";
import { candidatesFromMap, checkoutVerdict, pickOutCandidates } from "./avail.js";

const stayLimits = (state) => ({
  minStay: state.config?.minStay ?? 1,
  maxStayUi: state.config?.maxStayUi ?? 21,
});

// Кандидаты выезда для текущего заезда: lengths → checkouts → карта ({ list, pending, source })
export function outCandidates(state) {
  const { checkin } = state.dates;
  if (!checkin) return null;
  const adults = state.guests.adults;
  const local = candidatesFromMap(state.map, checkin, adults, stayLimits(state));
  return pickOutCandidates(state.lengths, state.checkouts, adults, local);
}

export const isOutCandidate = (state, date) =>
  Boolean(outCandidates(state)?.list.some((c) => c.to === date));

// Причина под календарём — одна короткая строка, видна на телефоне, в отличие от title
export function whyText(v, { checkin, date, adults }) {
  switch (v.code) {
    case "min":
      return `Минимум ${v.minStay} ${nightsWord(v.minStay)}: выезд с ${humanDay(addDays(checkin, v.minStay))}`;
    case "max":
      return `Не больше ${v.maxStayUi} ${plural(v.maxStayUi, "ночи", "ночей", "ночей")}: выезд до ${humanDay(addDays(checkin, v.maxStayUi))}`;
    case "closed":
      return `Продажи с ${humanDay(v.at)} ещё не открыты`;
    case "guests":
      return `Нет номеров на ${adults} ${adultsWord(adults)}`;
    case "pending":
      return `Проверяем выезд ${humanDay(date)}…`;
    default:
      return "На эти даты нет свободных номеров";
  }
}

// Заметку «Проверяем выезд…» показываем, только если цены идут дольше этого: в обычном случае
// ответ приходит раньше, и строка не мелькает (index.js открывает её таймером, shown: true)
export const PENDING_NOTE_DELAY_MS = 1000;

function note(state, date, v) {
  const { checkin } = state.dates;
  const adults = state.guests.adults;
  return {
    date,
    checkin,
    code: v.code,
    pending: v.code === "pending",
    shown: v.code !== "pending",
    text: whyText(v, { checkin, date, adults }),
  };
}

const RULE_CODES = new Set(["min", "max"]);

function verdict(state, checkin, date) {
  return checkoutVerdict(state.map, {
    checkin,
    date,
    adults: state.guests.adults,
    ...stayLimits(state),
  });
}

// Тап по дате позже заезда, которой нет среди кандидатов. Минимум и максимум ночей — правило,
// объясняем сразу. Наличие по карте — подсказка, а не приговор: закрытый в карте заезд не
// запрещает ночь проживания, а checkouts по SQLite осторожнее Шелтера — пока цены в пути,
// пишем «Проверяем…» и решаем по их приходу (resolvePending).
// Возвращает { why, src } — заметку под календарём и источник кандидатов для лога.
export function blockedTap(state, date) {
  const src = outCandidates(state)?.source ?? null;
  const v = verdict(state, state.dates.checkin, date);
  if (!v.ok && RULE_CODES.has(v.code)) return { why: note(state, date, v), src };
  if (state.lengths.loading) return { why: note(state, date, { code: "pending" }), src };
  return { why: note(state, date, v.ok ? { code: "server" } : v), src };
}

// Цены пришли (или упали), а гость ждал проверки выезда (pending): дата среди кандидатов —
// ставим выезд, нет — причина. null — ждать нечего (заметки нет, сменили заезд, цены в пути).
// event — строка для лога (calEventParams): выезд поставлен (src deferred) или причина.
export function resolvePending(state) {
  const w = state.ui.why;
  if (!w?.pending || w.checkin !== state.dates.checkin || state.lengths.loading) return null;
  if (isOutCandidate(state, w.date))
    return {
      checkout: w.date,
      why: null,
      event: { e: "out", date: w.date, src: "deferred", change: Boolean(state.dates.checkout) },
    };
  const { why } = blockedTap(state, w.date);
  return {
    checkout: state.dates.checkout,
    why,
    event: { e: "blocked", date: w.date, src: "lengths", reason: why.code },
  };
}

// Событие тапа по выезду для лога. Считать до store.set: он мержит в тот же объект
// состояния, и после него «был ли выезд» уже не узнать
export function pickOutEvent(state, date) {
  return {
    e: "out",
    date,
    src: outCandidates(state)?.source ?? null,
    change: Boolean(state.dates.checkout),
  };
}

// Шаг календаря: «in» — выбираем заезд, «out» — выезд. Без заезда — всегда «in»; с заездом —
// «out», пока гость не нажал чип «Заезд» (ui.calMode = "in"): тогда тап по доступной дате
// ставит новый заезд — и позже, и раньше текущего.
export function calStep(state) {
  if (!state.dates.checkin) return "in";
  return state.ui.calMode === "in" ? "in" : "out";
}

// Новый заезд при выбранном (перенос): выезд остаётся, если с новой даты он допустим по карте
// (цены уточнят — loadLengths снимет выезд с тостом). Нарушен минимум/максимум ночей — выезд
// снимаем и объясняем; карта против — снимаем на время проверки («Проверяем…»), цены нового
// заезда вернут его или объяснят отказ.
export function moveCheckin(state, date) {
  const { checkout } = state.dates;
  // заезд не раньше выезда — выезд просто снимаем, чип «Выезд» снова «—»
  if (!checkout || date >= checkout) return { dates: { checkin: date, checkout: null }, why: null };
  const v = verdict(state, date, checkout);
  if (v.ok) return { dates: { checkin: date, checkout }, why: null };
  const moved = { ...state, dates: { checkin: date, checkout: null } };
  if (!RULE_CODES.has(v.code))
    return { dates: moved.dates, why: note(moved, checkout, { code: "pending" }) };
  return { dates: moved.dates, why: note(moved, checkout, v) };
}

// Параметры события календаря для лога сервера (T37) — без ПДн: даты, взрослые, коды
export function calEventParams({ e, state, date, src, since, change, reason }) {
  const p = { e, from: state.dates.checkin, to: date, ad: state.guests.adults };
  if (src) p.src = src;
  if (reason) p.r = reason;
  if (since != null) p.ms = Math.max(0, Math.round(since));
  if (change) p.chg = 1;
  return p;
}
